import {
  TextareaAutosize,
  Typography,
  Button,
  CircularProgress,
  TextField,
  FormControlLabel,
  Checkbox,
  Snackbar,
  Alert,
  IconButton,
} from "@mui/material";
import "./AiTools.css";
import { useEffect, useState } from "react";
import { fal } from "@fal-ai/client";
import {
  NEGATIVE_PROMPT,
  FAL_KEY,
  FAL_MODEL,
  URLS,
  FAL_TRANSITION_API,
  AI_VIDEO_TRANSITION_DEFAULT_PROMPT,
} from "../../../constants";
import {
  Cancel,
  CheckCircle,
  Close,
  PhotoFilterRounded,
  SaveAltRounded,
  Videocam,
} from "@mui/icons-material";
import { uvenuFetcher } from "../../../utils/uvenu-fetcher";
import UvDialog from "../../../components/UI/UvDialog";
import SavedPrompts from "./SavedPrompts";
import { COLORS } from "../../../utils/colors";
import ConfirmationDialog from "../../../components/UI/ConfirmationDialog";
// const defaultResponse = {
//   seed: 2339060450,
//   images: [
//     {
//       url: "https://fal.media/files/elephant/Hatyu4nfLL6nFxL5C1Jd6.png",
//       width: 512,
//       height: 512,
//       content_type: "image/jpeg",
//     },
//   ],
//   prompt:
//     "A cute woman with pastel-colored hair and large sparkling eyes, wearing a flowing dress adorned with flowers, standing in a magical forest surrounded by glowing fireflies, pixar.",
//   timings: {
//     inference: 1.654581356793642,
//   },
//   has_nsfw_concepts: [false],
// };
const RenderSnackBar = ({ showToast, toastConfig, onClose }) => {
  const [show, setShow] = useState(showToast);
  useEffect(() => {
    setShow(showToast);
  }, [showToast]);
  return (
    <Snackbar
      open={show}
      autoHideDuration={toastConfig?.duration || 2000}
      onClose={() => onClose()}
      anchorOrigin={{ vertical: "top", horizontal: "center" }}
    >
      <Alert
        severity={toastConfig.type}
        variant="standard"
        icon={
          toastConfig.type === "success" ? (
            <CheckCircle sx={{ color: COLORS.success }} />
          ) : (
            <Cancel sx={{ color: COLORS.error }} />
          )
        }
      >
        {toastConfig.message}
        <>
          <IconButton
            size="small"
            aria-label="close"
            color="inherit"
            onClick={() => setShow(false)}
            sx={{ ml: 1 }}
          >
            <Close fontSize="small" />
          </IconButton>
        </>
      </Alert>
    </Snackbar>
  );
};
const AiTools = ({
  media,
  editedImage,
  onGenerated,
  eventDetails,
  onVideoGenerated,
}) => {
  const [promptList, setPromptList] = useState([]);
  const [selectedPrompt, setSelectedPrompt] = useState("");
  const [generatedResponse, setGeneratedResponse] = useState(null);
  const [generatedMedia, setGeneratedMedia] = useState(null);

  const [generating, setGenerating] = useState(null);
  const [prompt, setPrompt] = useState(selectedPrompt?.prompt || "");
  const [videoPrompt, setVideoPrompt] = useState(
    AI_VIDEO_TRANSITION_DEFAULT_PROMPT
  );
  const [source, setSource] = useState(null);
  const [hasNsfw, setHasNsfw] = useState(false);

  const [showSavePromptDialog, setShowSavePromptDialog] = useState(false);
  const [promptName, setPromptName] = useState("");

  const [search, setSearch] = useState("");
  const [totalPrompts, setTotalPrompts] = useState(0);
  const [allPrompts, setAllPrompts] = useState([]);
  const [isUseSeed, setIsUseSeedNumber] = useState(false);
  const [isGeneratingVideo, setIsGeneratingVideo] = useState(false);
  const [showToast, setShowToast] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  useEffect(() => {
    getPrompts();
  }, [eventDetails.event_id]);

  useEffect(() => {
    if (editedImage) {
      setSource(editedImage);
    } else if (media?.mediaFileUrl) {
      setSource(media?.mediaFileUrl);
    }
  }, [media, editedImage]);

  useEffect(() => {
    if (generatedMedia) {
      setGenerating(false);
    }
  }, [generatedMedia]);

  const getPrompts = async () => {
    try {
      const response = await uvenuFetcher({
        method: "GET",
        url: URLS.GET_PROMPTS(eventDetails.event_id),
        headers: {
          token: `${eventDetails.loggedInUserObj.token}`,
        },
      });
      if (response.statusCode === 200 || response.statusCode === 201) {
        const prompts = response?.json?.prompts;
        if (prompts && prompts.length) {
          setPromptList(response.json.prompts);
          setAllPrompts(response.json.prompts);
          setPrompt(response.json.prompts[0].prompt);
          setSelectedPrompt(response.json.prompts[0]);
          setTotalPrompts(response.json.total);
        }
      } else {
        console.log(response.json.message);
        return "error: " + response.json.message;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleSelectPrompt = (savedPrompt) => {
    setSelectedPrompt(savedPrompt);
    setPrompt(savedPrompt.prompt);
  };

  const handleSavePromptClick = async () => {
    if (!promptName) {
      alert("Please enter a prompt name");
      return;
    }

    try {
      const response = await uvenuFetcher({
        method: "POST",
        url: URLS.SAVE_PROMPT,
        body: {
          eventId: eventDetails.event_id,
          prompt,
          name: promptName,
          seed: generatedResponse?.seed,
          exampleImageUrl: generatedResponse?.images[0].url,
        },
        headers: {
          token: `${eventDetails.loggedInUserObj.token}`,
        },
      });
      if (response.statusCode === 200 || response.statusCode === 201) {
        getPrompts();
      } else {
        console.log(response.json.message);
        return "error: " + response.json.message;
      }
    } catch (error) {
      console.log(error);
    } finally {
      setPromptName("");
      setShowSavePromptDialog(false);
    }
  };

  const handleDeletePrompt = async (id) => {
    try {
      const response = await uvenuFetcher({
        method: "DELETE",
        url: URLS.DELETE_PROMPT(id),
        headers: {
          token: `${eventDetails.loggedInUserObj.token}`,
        },
      });
      if (response.statusCode === 200 || response.statusCode === 201) {
        getPrompts();
      } else {
        console.log(response.json.message);
        return "error: " + response.json.message;
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleGenerateClick = async () => {
    if (!prompt) {
      alert("Please enter a prompt");
      return;
    }
    if (!source) {
      alert("Please upload a media file");
      return;
    }
    fal.config({
      credentials: FAL_KEY,
    });
    setGeneratedMedia("");
    setGenerating(true);
    try {
      const input = {
        prompt,
        reference_image_url: source,
        image_size: {
          width: 512,
          height: 512,
        },
        negative_prompt: NEGATIVE_PROMPT,
      };
      if (selectedPrompt && selectedPrompt.seed && isUseSeed) {
        input["seed"] = selectedPrompt.seed;
      }
      const result = await fal.subscribe(FAL_MODEL, {
        input,
        logs: true,
        onQueueUpdate: (update) => {
          if (update.status === "IN_PROGRESS") {
            update.logs.map((log) => log.message).forEach(console.log);
          }
        },
      });

      if (result && result.data) {
        setGeneratedResponse(result.data);
        setGeneratedMedia(result.data.images[0].url);
        onGenerated(result.data.images[0]);
        if (
          result.data.has_nsfw_concepts &&
          result.data.has_nsfw_concepts.indexOf(true) !== -1
        ) {
          setHasNsfw(true);
        } else {
          setHasNsfw(false);
        }
        setGenerating(false);
      }
    } catch (error) {
      alert(error);
      setGenerating(false);
    }
  };
  const handleCreateVideoClick = async () => {
    setIsGeneratingVideo(true);
    setShowToast(true);
    fal.config({
      credentials: FAL_KEY,
    });
    const result = await fal.subscribe(FAL_TRANSITION_API, {
      input: {
        prompt: videoPrompt,
        first_image_url: source,
        last_image_url: generatedMedia,
        duration: "5",
        resolution: "540p",
        aspect_ratio: "1:1",
        seed: 4329664,
      },
      logs: true,
      onQueueUpdate: (update) => {
        if (update.status === "IN_PROGRESS") {
          update.logs.map((log) => log.message).forEach(console.log);
        }
      },
    });

    if (result && result.data) {
      setGeneratedResponse(result.data);
      onVideoGenerated(result.data.video);
    }
    setIsGeneratingVideo(false);
  };

  useEffect(() => {
    if (search) {
      const filteredPrompts = allPrompts.filter((prompt) => {
        return (
          (prompt?.name &&
            prompt?.name.toLowerCase().includes(search.toLowerCase())) ||
          (prompt?.prompt &&
            prompt?.prompt.toLowerCase().includes(search.toLowerCase()))
        );
      });
      setPromptList(filteredPrompts);
    } else {
      setPromptList(allPrompts);
    }
  }, [search, promptList, allPrompts]);

  const onSearch = (e) => {
    setSearch(e.target.value);
  };

  return (
    <div className="ait-main-container">
      {totalPrompts > 0 && (
        <div className="ait-saved-prompts">
          <Typography className="media-label" sx={{ my: 1 }}>
            Saved Prompts ({promptList.length})
          </Typography>
          <TextField
            sx={{ my: 1, color: "white" }}
            fullWidth
            value={search}
            onChange={onSearch}
            placeholder="Search name or prompt"
            InputProps={{
              style: {
                backgroundColor: "transparent",
                color: "white",
              },
            }}
          ></TextField>
          <SavedPrompts
            items={promptList}
            handleDeletePrompt={handleDeletePrompt}
            handleSelectPrompt={handleSelectPrompt}
          />
        </div>
      )}
      <div className="ai-tools-container">
        <div className="ait-media-container">
          <div className="source-media ait-media-box">
            <Typography className="media-label">Before</Typography>
            <div className="ait-media">
              <img
                src={source}
                alt="source-media"
                style={{ width: "100%", maxWidth: "100%" }}
              />
            </div>
          </div>
          <div className="generated-media ait-media-box">
            <Typography className="media-label">After</Typography>
            <div className="ait-media">
              {generating ? <CircularProgress /> : null}

              {generatedMedia ? (
                <img
                  src={generatedMedia}
                  alt="generted"
                  style={{ width: "100%", maxWidth: "100%" }}
                />
              ) : null}
              {hasNsfw && <p>NSFW Content</p>}
            </div>
          </div>
        </div>

        <TextareaAutosize
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          className="ait-prompt"
          placeholder="Enter Prompt"
          minRows={3}
          style={{
            backgroundColor: "transparent",
          }}
        ></TextareaAutosize>
        {generatedMedia && (
          <div style={{ width: "100%", display: "flex", flexDirection: "column" }}>
            <Typography className="media-label" sx={{ my: 1 }}>
              Video Prompt
            </Typography>
            <TextareaAutosize
              value={videoPrompt}
              onChange={(e) => setVideoPrompt(e.target.value)}
              className="ait-prompt"
              placeholder="Enter Video Prompt"
              minRows={2}
              style={{
                backgroundColor: "transparent",
              }}
            ></TextareaAutosize>
          </div>
        )}

        <FormControlLabel
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "white",
          }}
          disabled={generating || !selectedPrompt?.seed}
          control={
            <Checkbox
              checked={isUseSeed && selectedPrompt?.seed}
              onChange={(e) => {
                setIsUseSeedNumber(e.target.checked);
              }}
              name="useSeedNumber"
              color="primary"
            />
          }
          label="Generate Exact Style"
        />
        <div className="generate-container">
          <Button
            disabled={generating}
            variant="contained"
            sx={{ color: "white" }}
            startIcon={<PhotoFilterRounded />}
            onClick={handleGenerateClick}
          >
            Generate
          </Button>
          <Button
            disabled={
              generating !== false ||
              !prompt ||
              promptList.some((p) => p.prompt === prompt)
            }
            variant="contained"
            sx={{ color: "white", marginLeft: "16px" }}
            startIcon={<SaveAltRounded />}
            onClick={() => {
              setShowSavePromptDialog(true);
            }}
          >
            Save Prompt
          </Button>
          <Button
            disabled={!generatedMedia}
            variant="contained"
            sx={{ color: "white", marginLeft: "16px" }}
            startIcon={<Videocam />}
            onClick={() => {
              setShowConfirmation(true);
            }}
          >
            Create Video
          </Button>
        </div>

        <UvDialog
          show={showSavePromptDialog}
          onClose={() => {
            setPromptName("");
            setShowSavePromptDialog(false);
          }}
          onSubmit={handleSavePromptClick}
          title="Save Prompt"
          submitButtonLable="Save"
        >
          <TextField
            label="Prompt Name"
            variant="outlined"
            fullWidth
            value={promptName}
            onChange={(e) => {
              setPromptName(e.target.value);
            }}
            margin="normal"
            sx={{ marginBottom: "20px" }}
          />
        </UvDialog>
      </div>
      <RenderSnackBar
        showToast={showToast}
        onClose={() => setShowToast(false)}
        toastConfig={{
          type: "success",
          message:
            "Creating your video — this may take a moment. You’ll find it in Production Uploads when it’s done.",
          duration: 5000,
        }}
      />
      <ConfirmationDialog
        title="Create Video Confirmation"
        confirmButtonText="Yes Create"
        message={`Are you sure you want to create a video?`}
        show={showConfirmation}
        onConfirm={() => {
          handleCreateVideoClick();
          setShowConfirmation(false);
        }}
        onClose={() => {
          setShowConfirmation(false);
        }}
      />
    </div>
  );
};
export default AiTools;
